import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { Trophy } from 'lucide-react'
import { ReactNode } from 'react'

import { SUPPORT_REASON_LABEL } from '@/app/entities/SupportRequest'
import { SupportOverviewMember } from '@/app/services/supportRequestService/getSupportOverviewStats'
import { cn } from '@/app/utils/cn'
import { DateRangePicker } from '@/view/components/DateRangePicker'
import { Skeleton } from '@/view/components/ui/skeleton'

import { AttendanceChart } from '../SupportAttendanceStats/AttendanceChart'
import { PeopleFilter } from './PeopleFilter'
import { useSupportStatistics } from './useSupportStatistics'

const formatNumber = (value: number) =>
  value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

interface CardProps {
  label: string
  value: number
  hint?: string
}

const Card = ({ label, value, hint }: CardProps) => (
  <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-gray01 bg-white px-4 py-3">
    <span className="text-xs font-medium leading-tight text-black04">
      {label}
    </span>
    <span className="text-2xl font-semibold text-black01">
      {formatNumber(value)}
    </span>
    {hint && (
      <span className="text-[11px] leading-tight text-black04">{hint}</span>
    )}
  </div>
)

const Panel = ({
  title,
  children,
  className,
}: {
  title: string
  children: React.ReactNode
  className?: string
}) => (
  <section
    className={cn(
      'flex min-w-0 flex-col gap-3 rounded-xl border bg-card p-4',
      className,
    )}
  >
    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </span>
    {children}
  </section>
)

const MEDAL_CLASSNAME = ['text-amber-500', 'text-slate-400', 'text-orange-700']

const Ranking = ({ members }: { members: SupportOverviewMember[] }) => {
  if (members.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-black04">
        Nenhum membro do suporte encontrado
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-black04">
            <th className="w-10 py-2 font-medium">#</th>
            <th className="py-2 font-medium">Membro</th>
            <th className="py-2 text-right font-medium">Atendidos</th>
            <th className="py-2 text-right font-medium">Em atendimento</th>
            <th className="py-2 text-right font-medium">Abertos</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member, index) => (
            <tr key={member.id} className="border-b last:border-b-0">
              <td className="py-2.5 font-semibold text-black04">
                {member.attended > 0 && index < 3 ? (
                  <Trophy
                    className={cn('h-4 w-4', MEDAL_CLASSNAME[index])}
                    aria-label={`${index + 1}º lugar`}
                  />
                ) : (
                  index + 1
                )}
              </td>
              <td className="max-w-[200px] truncate py-2.5 font-medium text-black01">
                {member.name}
              </td>
              <td className="py-2.5 text-right font-semibold text-black01">
                {member.attended}
              </td>
              <td className="py-2.5 text-right text-black04">
                {member.inProgress}
              </td>
              <td className="py-2.5 text-right text-black04">{member.open}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const Reasons = ({
  reasons,
}: {
  reasons: { reason: keyof typeof SUPPORT_REASON_LABEL; count: number }[]
}) => {
  const total = reasons.reduce((sum, item) => sum + item.count, 0)

  if (total === 0) {
    return (
      <p className="py-6 text-center text-sm text-black04">
        Nenhum chamado aberto no período
      </p>
    )
  }

  return (
    <ul className="flex flex-col gap-3">
      {reasons.map(({ reason, count }) => {
        const percent = Math.round((count / total) * 100)
        return (
          <li key={reason} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate font-medium text-black01">
                {SUPPORT_REASON_LABEL[reason] ?? reason}
              </span>
              <span className="shrink-0 text-xs text-black04">
                {count} · {percent}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray02">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.max(percent, 2)}%` }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

interface SupportStatisticsProps {
  people: { id: string; name: string }[]
  toolbar?: ReactNode
}

export const SupportStatistics = ({
  people,
  toolbar,
}: SupportStatisticsProps) => {
  const {
    data,
    isLoading,
    isError,
    isFetching,
    range,
    changeRange,
    members,
    toggleMember,
    clearMembers,
    maxDays,
  } = useSupportStatistics()

  const rangeDays =
    range.from && range.to
      ? differenceInCalendarDays(range.to, range.from) + 1
      : 1
  const tooLong = rangeDays > maxDays

  return (
    <div className="flex flex-col gap-4 pb-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col">
          <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Estatísticas de chamados
          </span>
          {data && (
            <span className="text-xs text-black04">
              {format(parseISO(data.from), 'dd/MM/yyyy')} a{' '}
              {format(parseISO(data.to), 'dd/MM/yyyy')}
              {members.length > 0 &&
                ` · ${members.length} ${members.length === 1 ? 'pessoa' : 'pessoas'}`}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <DateRangePicker
            date={range}
            onDateChange={changeRange}
            numberOfMonths={1}
            className="sm:w-[240px]"
          />
          <PeopleFilter
            people={people}
            selected={members}
            onToggle={toggleMember}
            onClear={clearMembers}
          />
        </div>
      </div>

      {tooLong ? (
        <>
          <p className="rounded-xl border border-dashed border-gray01 bg-gray-50 p-4 text-center text-sm text-black04">
            Escolha um período de até {maxDays} dias
          </p>
          {toolbar}
        </>
      ) : isError ? (
        <>
          <p className="rounded-xl border border-dashed border-gray01 bg-gray-50 p-4 text-center text-sm text-black04">
            Não foi possível carregar as estatísticas
          </p>
          {toolbar}
        </>
      ) : isLoading || !data ? (
        <>
          <Skeleton className="h-[480px] w-full rounded-xl" />
          {toolbar}
        </>
      ) : (
        <>
          <div
            className={cn(
              'grid grid-cols-2 gap-3 transition-opacity lg:grid-cols-4',
              isFetching && 'opacity-60',
            )}
          >
            <Card
              label="Atendidos no período"
              value={data.indicators.attended}
              hint={`${data.indicators.attendants} ${data.indicators.attendants === 1 ? 'atendente' : 'atendentes'}`}
            />
            <Card
              label="Média por atendente"
              value={data.indicators.averageAttended}
            />
            <Card label="Abertos no período" value={data.indicators.opened} />
            <Card
              label="Aguardando atendimento"
              value={data.indicators.openNow}
              hint={`${data.indicators.unassignedNow} sem responsável · ${data.indicators.inProgressNow} em atendimento`}
            />
          </div>

          <Panel title="Chamados por dia">
            <AttendanceChart
              days={data.days}
              barLabel={
                members.length
                  ? 'Atendidos pela seleção'
                  : 'Atendidos pela equipe'
              }
            />
          </Panel>

          {toolbar}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Panel title="Ranking por membro">
              <Ranking members={data.ranking} />
            </Panel>
            <Panel title="Problemas mais relatados">
              <Reasons reasons={data.reasons} />
            </Panel>
          </div>
        </>
      )}
    </div>
  )
}
